import { crearAlertaViaje, deshabilitarAlertaViaje } from "../../../api/api-alerta-viaje";
import { TIPO_INPUT } from "../tipo_input";

const parseMetaFromItem = (item) => {
    if (Array.isArray(item?.meta)) return item.meta;
    if (typeof item?.meta === "string" && item.meta.trim().length > 0) {
        try {
            const parsed = JSON.parse(item.meta);
            return Array.isArray(parsed) ? parsed : [];
        } catch (err) {
            return [];
        }
    }
    return [];
};

const parseRespuestasFromItem = (item) => {
    if (Array.isArray(item?.respuestasParsed)) return item.respuestasParsed;
    if (Array.isArray(item?.respuestas)) return item.respuestas;
    if (typeof item?.respuestas === "string" && item.respuestas.trim().length > 0) {
        try {
            const parsed = JSON.parse(item.respuestas);
            return Array.isArray(parsed) ? parsed : [];
        } catch (err) {
            return [];
        }
    }
    return [];
};

const extraerValor = (raw) => {
    if (raw == null) return null;
    if (typeof raw === "object") return raw.value ?? null;
    return raw;
};

const extraerLabel = (raw) => {
    if (raw == null) return null;
    if (typeof raw === "object") {
        return typeof raw.label === "string" ? raw.label : null;
    }
    return null;
};

// Extractor de card de productos (usa item.meta con TIPO_INPUT como discriminador).
export const extraerDatosCardProductos = (item) => {
    const meta = parseMetaFromItem(item);
    const buscarPorTipo = (tipo) => meta.find((m) => m?.type === tipo)?.value ?? null;

    const origenRaw = buscarPorTipo(TIPO_INPUT.GEOCERCA_ORIGEN);
    const destinoRaw = buscarPorTipo(TIPO_INPUT.GEOCERCA_DESTINO);
    const georutaRaw = buscarPorTipo(TIPO_INPUT.GEORUTA);
    const placaRaw = buscarPorTipo(TIPO_INPUT.SELECCIONE_VEHICULO);
    const horaIniRaw = buscarPorTipo(TIPO_INPUT.HORA_SALIDA);
    const horaFinRaw = buscarPorTipo(TIPO_INPUT.HORA_LLEGADA);

    return {
        geocercaOrigenId: extraerValor(origenRaw),
        geocercaOrigenLabel: extraerLabel(origenRaw),
        geocercaDestinoId: extraerValor(destinoRaw),
        geocercaDestinoLabel: extraerLabel(destinoRaw),
        georutaId: extraerValor(georutaRaw),
        georutaLabel: extraerLabel(georutaRaw),
        placa: (() => {
            const v = extraerValor(placaRaw);
            return v != null ? String(v) : null;
        })(),
        horaIni: (() => {
            const v = extraerValor(horaIniRaw);
            return v != null ? String(v) : null;
        })(),
        horaFin: (() => {
            const v = extraerValor(horaFinRaw);
            return v != null ? String(v) : null;
        })(),
    };
};

// Extractor de card de camiones (usa item.respuestasParsed con flags semánticos).
// Las `key` de cada respuesta las define quien creó el formulario, así que no son
// estables. Los flags booleanos (geocerca_origen, hora_inicio_viaje, etc.) los pone
// el diseñador del formulario y sí identifican el rol lógico del campo.
export const extraerDatosCardCamiones = (item) => {
    const respuestas = parseRespuestasFromItem(item);
    const buscarPorFlag = (flag) =>
        respuestas.find((r) => r?.[flag] === true)?.value ?? null;

    const origenRaw = buscarPorFlag("geocerca_origen");
    const destinoRaw = buscarPorFlag("geocerca_destino");
    const georutaRaw = buscarPorFlag("georuta");
    const placaRaw = buscarPorFlag("vehiculo_viaje");
    const horaIniRaw = buscarPorFlag("hora_inicio_viaje");
    const horaFinRaw = buscarPorFlag("hora_fin_viaje");

    return {
        geocercaOrigenId: extraerValor(origenRaw),
        geocercaOrigenLabel: extraerLabel(origenRaw),
        geocercaDestinoId: extraerValor(destinoRaw),
        geocercaDestinoLabel: extraerLabel(destinoRaw),
        georutaId: extraerValor(georutaRaw),
        georutaLabel: extraerLabel(georutaRaw),
        placa: (() => {
            const v = extraerValor(placaRaw);
            return v != null ? String(v) : null;
        })(),
        horaIni: (() => {
            const v = extraerValor(horaIniRaw);
            return v != null ? String(v) : null;
        })(),
        horaFin: (() => {
            const v = extraerValor(horaFinRaw);
            return v != null ? String(v) : null;
        })(),
    };
};

const buscarLabelGeocerca = (id, listaGeocercas) => {
    if (id == null) return null;
    const idStr = String(id);
    const encontrado = (Array.isArray(listaGeocercas) ? listaGeocercas : []).find(
        (g) => String(g?.id) === idStr || String(g?.value) === idStr
    );
    if (!encontrado) return null;
    return (
        encontrado.nombre ??
        encontrado.descripcion ??
        encontrado.label ??
        null
    );
};

const buscarLabelGeoruta = (id, listaGeorutas) => {
    if (id == null) return null;
    const idStr = String(id);
    const encontrado = (Array.isArray(listaGeorutas) ? listaGeorutas : []).find(
        (g) => String(g?.id) === idStr || String(g?.value) === idStr
    );
    if (!encontrado) return null;
    return (
        encontrado.nombre ??
        encontrado.descripcion ??
        encontrado.label ??
        null
    );
};

const construirPayloadAlerta = ({
    geocercaValue,
    geocercaLabel,
    placa,
    horaIni,
    horaFin,
    correos,
    telefonos,
}) => {
    const arrValuesGeocercas = geocercaValue != null
        ? [{ value: geocercaValue, label: geocercaLabel ?? String(geocercaValue) }]
        : [];
    const arrValuesPlacas = placa != null && placa !== ""
        ? [{ value: placa, label: placa }]
        : [];
    const horaIniFinal = horaIni ?? "00:00";
    const horaFinFinal = horaFin ?? "23:59";

    return {
        tipoAlerta: "tipoalerta_geocerca",
        horaIni: horaIniFinal,
        horaFin: horaFinFinal,
        severidad: "media",
        correosDestino: correos,
        telefonosDestino: telefonos,
        configAlerta: {
            alerta_diasem0: 1,
            alerta_diasem1: 1,
            alerta_diasem2: 1,
            alerta_diasem3: 1,
            alerta_diasem4: 1,
            alerta_diasem5: 1,
            alerta_diasem6: 1,
            arrValuesGeocercas,
            arrValuesPlacas,
            correos,
            geocercaSeleccionada: geocercaValue ?? null,
            horaIni: horaIniFinal,
            horaFin: horaFinFinal,
            intervaloEntreAlertas: 0,
            planillaObjetoNom: placa != null && placa !== "" ? String(placa) : "",
            telefonosDestino: telefonos,
            tiempoPermanencia: 0,
            tipoAlerta: "tipoalerta_geocerca",
            tipoMovimientoGeocerca: "IN/OUT",
            severidad: "media",
        },
    };
};

/**
 * Crea las alertas (origen, destino, georuta) para una card que entra a "Iniciar viaje".
 * La alerta de georuta solo se crea si la card tiene georuta.
 * Semántica todo-o-nada: si alguna alerta falla, se hace rollback deshabilitando
 * las que sí se crearon.
 * @returns {{ success, alertasIniciarViaje, errores, erroresRollback }}
 *   - success = true → todas las alertas se crearon; alertasIniciarViaje trae los IDs.
 *   - success = false → alguna falló; alertasIniciarViaje = null; errores contiene los fallos;
 *     erroresRollback contiene los IDs que quedaron huérfanos si el rollback no logró deshabilitarlos.
 */
export const crearAlertasIniciarViaje = async ({
    cardData,
    listaGeocercas,
    listaGeorutas,
    destinos,
}) => {
    const { correos = [], telefonos = [] } = destinos ?? {};
    const errores = [];

    const objetivos = [
        {
            key: "origen",
            geocercaValue: cardData?.geocercaOrigenId,
            geocercaLabel:
                cardData?.geocercaOrigenLabel ??
                buscarLabelGeocerca(cardData?.geocercaOrigenId, listaGeocercas),
        },
        {
            key: "destino",
            geocercaValue: cardData?.geocercaDestinoId,
            geocercaLabel:
                cardData?.geocercaDestinoLabel ??
                buscarLabelGeocerca(cardData?.geocercaDestinoId, listaGeocercas),
        },
    ];

    if (cardData?.georutaId != null) {
        objetivos.push({
            key: "georuta",
            geocercaValue: cardData.georutaId,
            geocercaLabel:
                cardData.georutaLabel ??
                buscarLabelGeoruta(cardData.georutaId, listaGeorutas),
        });
    }

    const resultados = await Promise.all(
        objetivos.map((obj) =>
            crearAlertaViaje(
                construirPayloadAlerta({
                    geocercaValue: obj.geocercaValue,
                    geocercaLabel: obj.geocercaLabel,
                    placa: cardData?.placa ?? null,
                    horaIni: cardData?.horaIni,
                    horaFin: cardData?.horaFin,
                    correos,
                    telefonos,
                })
            )
        )
    );

    const idsCreados = { origen: null, destino: null, georuta: null };
    resultados.forEach((resp, idx) => {
        const { key } = objetivos[idx];
        if (resp?.error || resp?.id == null) {
            errores.push({ key, message: resp?.message ?? "id no devuelto" });
        } else {
            idsCreados[key] = resp.id;
        }
    });

    if (errores.length === 0) {
        return { success: true, alertasIniciarViaje: idsCreados, errores: [], erroresRollback: [] };
    }

    // Rollback: deshabilitar las alertas que sí se crearon.
    const paraDeshabilitar = Object.entries(idsCreados)
        .filter(([, id]) => id != null)
        .map(([key, id]) => ({ key, id }));
    const rollbackResp = await Promise.all(
        paraDeshabilitar.map((entry) => deshabilitarAlertaViaje(entry.id))
    );
    const erroresRollback = [];
    rollbackResp.forEach((resp, idx) => {
        if (resp?.error) {
            erroresRollback.push({
                key: paraDeshabilitar[idx].key,
                id: paraDeshabilitar[idx].id,
                message: resp?.message ?? "no se pudo deshabilitar en rollback",
            });
        }
    });

    return { success: false, alertasIniciarViaje: null, errores, erroresRollback };
};

/**
 * Deshabilita las alertas persistidas en un item cuando sale de "Iniciar viaje".
 * Semántica todo-o-nada: si alguna deshabilitación falla, la card no se debe mover.
 * @returns {{ success, alertasRestantes, errores }}
 *   - success = true → todas las alertas fueron deshabilitadas.
 *   - success = false → alguna falló; alertasRestantes contiene solo los IDs de las que
 *     siguen activas (para persistir en el item y reintentar luego).
 */
export const deshabilitarAlertasIniciarViaje = async (alertasIniciarViaje) => {
    if (!alertasIniciarViaje || typeof alertasIniciarViaje !== "object") {
        return { success: true, alertasRestantes: null, errores: [] };
    }
    const ids = ["origen", "destino", "georuta"]
        .map((k) => ({ key: k, id: alertasIniciarViaje[k] }))
        .filter((entry) => entry.id != null);

    if (ids.length === 0) {
        return { success: true, alertasRestantes: null, errores: [] };
    }

    const resultados = await Promise.all(
        ids.map((entry) => deshabilitarAlertaViaje(entry.id))
    );
    const errores = [];
    const alertasRestantes = { origen: null, destino: null, georuta: null };
    resultados.forEach((resp, idx) => {
        const { key, id } = ids[idx];
        if (resp?.error) {
            errores.push({ key, id, message: resp?.message ?? "error" });
            alertasRestantes[key] = id;
        }
    });
    if (errores.length === 0) {
        return { success: true, alertasRestantes: null, errores: [] };
    }
    return { success: false, alertasRestantes, errores };
};
